/**
 * STAGE 6 星界·终焉 —— 综合高密度：弹幕变换（蛇形/分裂/加速）+ 阶段演出。
 * 小兵射型：3 连狙（star）/ 蛇形 3 路（eye）/ 大环（mass）/ 分裂弹 + 5 路（cacodemon）。
 */
import * as P from '../../game/patterns.js';
import { aimOf, fireTimer, zigzagBullet, splitBullet, trailBullet, s6Mid, s6Big } from './bosses.js';

const DROP_STD = [{ type: 'P', weight: 2 }, { type: 'POINT', weight: 3 }]; // P 占比 40%
const DROP_POINT = [{ type: 'POINT', weight: 1 }];
const DROP_ELITE = [{ type: 'B', weight: 1 }, { type: 'STAR', weight: 1 }, { type: 'P', weight: 2 }]; // 精英必掉半数为 P

/** 星精：直线 + 3 连狙 */
function star(x, y, vx, vy) {
  return {
    x, y, hp: 20, r: 12, spriteKey: 's6_star', scoreValue: 400, drops: DROP_STD,
    script(e, dt, ctx) {
      e.x += vx * dt * 60;
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 340 && fireTimer(e, dt, 1.4)) {
        // 彗尾 3 连狙：弹后拖渐隐星尘尾（星界的语言是光与尾迹）
        P.aimed(ctx.field, e.x, e.y, e.age, aimOf(ctx, e.x, e.y), { n: 3, speed: 3.0, color: 'star_yellow', jitter: 0.05, onUpdate: trailBullet(ctx.field) });
      }
    },
  };
}

/** 魔眼：正弦 + 蛇形 3 路 */
function eye(x, y, vy) {
  return {
    x, y, hp: 24, r: 13, spriteKey: 's6_eye', scoreValue: 500, drops: DROP_POINT,
    script(e, dt, ctx) {
      e.x += Math.sin(e.age * 1.7) * 0.9 * dt * 60;
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 340 && fireTimer(e, dt, 1.6)) {
        const aim = aimOf(ctx, e.x, e.y);
        for (const k of [-1, 0, 1]) {
          ctx.field.spawn({
            x: e.x, y: e.y, speed: 2.4, angle: aim + k * 0.22, r: 4,
            color: 'star_cyan', onUpdate: zigzagBullet(2.2, 4),
          });
        }
      }
    },
  };
}

/** 星团：纵队慢压 + 大环 */
function mass(x, y, vy) {
  return {
    x, y, hp: 30, r: 15, spriteKey: 's6_mass', scoreValue: 600, drops: DROP_STD,
    script(e, dt, ctx) {
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 340 && fireTimer(e, dt, 2.0)) {
        P.ring(ctx.field, e.x, e.y, e.age, 0, { n: 12, speed: 2.0, offset: e.age * 0.5, color: 'star_purple' });
      }
    },
  };
}

/** 卡可魔精英：悬停分裂弹 + 5 路扇 */
function cacodemon(x) {
  return {
    x, y: -28, hp: 40, r: 17, spriteKey: 's6_cacodemon', scoreValue: 1000, drops: DROP_ELITE,
    script(e, dt, ctx) {
      if (e.y < 140) {
        e.y += 1.7 * dt * 60;
      } else if ((e._hold = (e._hold || 0) + dt) < 4.5) {
        e.x += Math.sin(e.age * 1.4) * 0.7 * dt * 60;
        if (fireTimer(e, dt, 2.2)) {
          const aim = aimOf(ctx, e.x, e.y);
          ctx.field.spawn({
            x: e.x, y: e.y, speed: 2.0, angle: aim, r: 5, color: 'star_yellow',
            onUpdate: splitBullet(ctx.field, 0.9, 0.5, 'star_pink'),
          });
        }
        if (fireTimer(e, dt, 1.5, '_ft2')) {
          P.spread(ctx.field, e.x, e.y, e.age, aimOf(ctx, e.x, e.y), { n: 5, arc: 0.7, speed: 2.6, color: 'orb_red' });
        }
      } else {
        e.y += 2.6 * dt * 60;
      }
    },
  };
}

export default {
  id: 6, name: '星界·终焉', bg: 'bg6', bgm: 'stage6',
  *script(api) {
    const W = api.VIEW_W;
    yield { say: 'STAGE 6  星界·终焉' };
    yield { wait: 1.0 };

    // W1 星精纵队
    yield { wave: [0, 1, 2, 3].map((i) => star(W / 2, -24 - i * 44, 0, 1.8)) };
    yield { wait: 3.5 };
    // W2 魔眼横排
    yield { wave: [0, 1, 2, 3].map((i) => eye(80 + i * 105, -24, 1.1)) };
    yield { wait: 4.0 };
    // W3 交叉 + 星团混编
    yield {
      wave: [
        ...[0, 1].map((i) => star(60 + i * 50, -24 - i * 40, 1.2, 1.8)),
        ...[0, 1].map((i) => star(W - 60 - i * 50, -24 - i * 40, -1.2, 1.8)),
        mass(W / 2, -60, 1.0),
      ],
    };
    yield { wait: 4.0 };
    // W4 精英卡可魔
    yield { wave: [cacodemon(W / 2)] };
    yield { clear: true };
    yield { wait: 1.0 };

    yield { boss: s6Mid() };
    yield { wait: 2.0 };

    // W5 包夹（道中二，强度上升）
    yield {
      wave: [
        ...[0, 1, 2].map((i) => star(-20, 80 + i * 44, 1.7, 0.5)),
        ...[0, 1, 2].map((i) => star(W + 20, 100 + i * 44, -1.7, 0.5)),
      ],
    };
    yield { wait: 4.0 };
    // W6 魔眼 + 星团混编
    yield {
      wave: [
        ...[0, 1, 2].map((i) => eye(90 + i * 150, -24, 1.2)),
        mass(150, -70, 1.1),
        mass(W - 150, -70, 1.1),
      ],
    };
    yield { wait: 4.0 };
    // W7 双卡可魔
    yield { wave: [cacodemon(140), cacodemon(W - 140)] };
    yield { clear: true };
    yield { wait: 1.0 };

    yield { boss: s6Big() };
    yield { say: 'STAGE 6 CLEAR' };
  },
};
